/** Super admin › Plans & limits: every plan's prices, allowances and caps (backend domain/plans.py), stored over the
 * ECLIPSE defaults. Saving PATCHes each changed plan (audited `plan.update`); companies get the new values on their
 * next request. Empty = the item's null meaning (unlimited, talk to us, contract…). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { api, errorMessage, unwrap } from "../../api/client";
import { Alert, Badge, Button, Card, Modal, PageHead, Spinner, useToast } from "../../components/ui";
import { FormattedMessage } from "react-intl";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { TIERS, byArea, fromInput, isTiered, planChanges, switchChanges, toInput, type PlanItem, type Tier } from "../../lib/plans";
import { useFmt } from "./format";
import { LeaveGuard } from "../../lib/leaveGuard";

type Drafts = Record<string, Record<string, unknown>>;

const TIER_KEY: Record<Tier, MessageKey> = {
  unverified: "admin.plans.tierUnverified",
  domain: "admin.plans.tierDomain",
  business: "admin.plans.tierBusiness",
};

/** A plan value as people read it (the page's own wording of lib/plans' formatValue, in the reader's language). */
function useFormatValue() {
  const t = useT();
  const { num, money, lux, percent } = useFmt();
  const one = (item: Pick<PlanItem, "type" | "none_label">, value: unknown): string => {
    if (value === null || value === undefined) return item.none_label;
    if (item.type === "flag") return value ? t("admin.plans.on") : t("admin.plans.off");
    if (typeof value !== "number") return String(value);
    if (item.type === "cents") return money(value / 100, "USD", value % 100 ? 2 : 0);
    if (item.type === "lux") return t("admin.plans.luxValue", { lux: lux(value) });
    if (item.type === "days") return t("admin.plans.daysValue", { count: value });
    if (item.type === "percent") return percent(value);
    if (item.type === "priority") return t("admin.plans.priorityValue", { level: value <= 10 ? "high" : value <= 50 ? "standard" : "low", value });
    return num(value);
  };
  return (item: Pick<PlanItem, "type" | "none_label">, value: unknown): string =>
    isTiered(value)
      ? TIERS.filter((tier) => tier in value)
          .map((tier) => t("admin.plans.tierValue", { tier, value: one(item, value[tier] ?? null) }))
          .join(" · ")
      : one(item, value);
}

export function usePlanCatalogue() {
  return useQuery({ queryKey: ["admin-plans"], queryFn: () => unwrap(api.GET("/v1/admin/plans")) });
}

function ValueInput({ item, value, onChange, invalid, small }: {
  item: PlanItem;
  value: number | boolean | null | undefined;
  onChange: (v: number | boolean | null | undefined, text: string) => void;
  invalid?: boolean;
  small?: string;
}) {
  const t = useT();
  const [text, setText] = useState(toInput(item, value));
  useEffect(() => {
    if (fromInput(item, text) !== value) setText(toInput(item, value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  if (item.type === "flag")
    return (
      <select className="select plan-input" value={value ? "on" : "off"} onChange={(e) => onChange(e.target.value === "on", e.target.value)}>
        <option value="on">{t("admin.plans.on")}</option>
        <option value="off">{t("admin.plans.off")}</option>
      </select>
    );
  return (
    <span className="plan-input-wrap">
      {small && <small className="faint">{small}</small>}
      <input
        className={`input plan-input${invalid ? " is-invalid" : ""}`}
        value={text}
        placeholder={item.none_label}
        inputMode={item.type === "cents" ? "decimal" : "numeric"}
        aria-label={item.label}
        onChange={(e) => {
          setText(e.target.value);
          onChange(fromInput(item, e.target.value), e.target.value);
        }}
      />
    </span>
  );
}

export function AdminPlans() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const { date } = useFmt();
  const formatValue = useFormatValue();
  const catalogue = usePlanCatalogue();
  const [drafts, setDrafts] = useState<Drafts>({});
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  // Limits switched off (not enforced) per plan.
  const [off, setOff] = useState<Record<string, string[]>>({});
  const reset = () => {
    if (!catalogue.data) return;
    setDrafts(Object.fromEntries(catalogue.data.plans.map((p) => [p.key, structuredClone(p.values)])));
    setOff(Object.fromEntries(catalogue.data.plans.map((p) => [p.key, [...p.disabled]])));
  };
  useEffect(reset, [catalogue.data]);
  const save = useMutation({
    mutationFn: async () => {
      for (const plan of catalogue.data!.plans) {
        const { values, reset } = planChanges(plan.values, drafts[plan.key] ?? {}, plan.defaults);
        const { disable, enable } = switchChanges(plan.disabled, off[plan.key] ?? []);
        if (!Object.keys(values).length && !reset.length && !disable.length && !enable.length) continue;
        await unwrap(
          api.PATCH("/v1/admin/plans/{plan_key}", { params: { path: { plan_key: plan.key } }, body: { values, reset, disable, enable } }),
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      toast(t("admin.plans.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (catalogue.isLoading || !catalogue.data) return catalogue.error ? <Alert tone="danger">{errorMessage(catalogue.error)}</Alert> : <Spinner />;
  const { plans, items } = catalogue.data;
  const dirty = plans.some(
    (p) => JSON.stringify(drafts[p.key]) !== JSON.stringify(p.values) || JSON.stringify([...(off[p.key] ?? [])].sort()) !== JSON.stringify([...p.disabled].sort()),
  );
  const toggle = (plan: string, key: string, enforced: boolean) =>
    setOff((o) => ({ ...o, [plan]: enforced ? (o[plan] ?? []).filter((k) => k !== key) : [...(o[plan] ?? []), key] }));
  const set = (plan: string, key: string, value: unknown, valid: boolean, cell: string) => {
    setDrafts((d) => ({ ...d, [plan]: { ...d[plan], [key]: value } }));
    setInvalid((s) => {
      const next = new Set(s);
      if (valid) next.delete(cell);
      else next.add(cell);
      return next;
    });
  };
  // Published values (landing pricing page, Terms) changed in this save: confirm first.
  const publishedChanges = plans.flatMap((p) =>
    items
      .filter((i) => i.published && JSON.stringify(drafts[p.key]?.[i.key]) !== JSON.stringify(p.values[i.key]))
      .map((i) => ({ plan: p.name, item: i, from: p.values[i.key], to: drafts[p.key]?.[i.key] })),
  );
  const places = [...new Set(publishedChanges.flatMap((c) => c.item.announced_in))];
  const actions = (
    <div className="row">
      <Button variant="ghost" icon={<RotateCcw />} disabled={!dirty} onClick={reset}>
        {t("admin.plans.discard")}
      </Button>
      <Button variant="primary" icon={<Save />} disabled={!dirty || invalid.size > 0} loading={save.isPending} onClick={() => (publishedChanges.length ? setConfirming(true) : save.mutate())}>
        {t("admin.plans.save")}
      </Button>
    </div>
  );
  return (
    <>
      <LeaveGuard dirty={dirty} />
      <PageHead
        title={t("admin.plans.title")}
        description={t("admin.plans.description", { date: date("2026-10-13T00:00:00") })}
        actions={actions}
      />
      {invalid.size > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="danger">{t("admin.plans.invalid")}</Alert>
        </div>
      )}
      <div className="stack">
        {byArea(items).map(([area, group]) => (
          <Card key={area} title={<span dir="auto">{area}</span>} pad={false}>
            <div className="table-wrap">
              <table className="table plans-table">
                <thead>
                  <tr>
                    <th>{t("admin.plans.colItem")}</th>
                    {plans.map((p) => (
                      <th key={p.key}>{p.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.map((item) => (
                    <tr key={item.key}>
                      <td>
                        <div>
                          <span dir="auto">{item.label}</span>{" "}
                          {item.published && (
                            <Badge tone="info" plain>
                              {t("admin.plans.published")}
                            </Badge>
                          )}
                        </div>
                        {item.description && <div className="muted small" dir="auto">{item.description}</div>}
                        <div className="faint small mono" dir="ltr">{item.key}</div>
                      </td>
                      {plans.map((p) => {
                        const value = drafts[p.key]?.[item.key];
                        const enforced = !(off[p.key] ?? []).includes(item.key);
                        const changed = JSON.stringify(value) !== JSON.stringify(p.defaults[item.key]) || !enforced;
                        const cell = `${p.key}:${item.key}`;
                        return (
                          <td key={p.key} className={changed ? "plan-changed" : undefined} title={t("admin.plans.defaultValue", { value: formatValue(item, p.defaults[item.key]) })}>
                            {item.tiered && p.key === "free" && isTiered(value) ? (
                              <div className="plan-tiers">
                                {TIERS.map((tier: Tier) => (
                                  <ValueInput
                                    key={tier}
                                    item={item}
                                    small={t(TIER_KEY[tier])}
                                    value={value[tier]}
                                    invalid={invalid.has(`${cell}:${tier}`)}
                                    onChange={(v) => set(p.key, item.key, { ...value, [tier]: v === undefined ? value[tier] : v }, v !== undefined, `${cell}:${tier}`)}
                                  />
                                ))}
                              </div>
                            ) : (
                              <ValueInput
                                item={item}
                                value={value as number | boolean | null}
                                invalid={invalid.has(cell)}
                                onChange={(v) => set(p.key, item.key, v === undefined ? value : v, v !== undefined, cell)}
                              />
                            )}
                            {item.can_disable && (
                              <label className="plan-enforced small">
                                <input type="checkbox" checked={enforced} onChange={(e) => toggle(p.key, item.key, e.target.checked)} /> {t("admin.plans.enforced")}
                              </label>
                            )}
                            {changed && <Badge tone="warn" plain>{enforced ? t("admin.plans.changed") : t("admin.plans.notEnforced")}</Badge>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>
      {confirming && (
        <Modal
          title={t("admin.plans.confirmTitle")}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                {t("admin.common.cancel")}
              </Button>
              <Button
                variant="primary"
                loading={save.isPending}
                onClick={() => {
                  setConfirming(false);
                  save.mutate();
                }}
              >
                {t("admin.plans.saveAnyway")}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p style={{ margin: 0 }}>
              {t("admin.plans.confirmAnnounced", { count: publishedChanges.length, places: intl.formatList(places, { type: "conjunction" }) })}{" "}
              {t("admin.plans.confirmUpdate", { count: places.length })}
            </p>
            <ul className="small" style={{ margin: 0, paddingInlineStart: 18 }}>
              {publishedChanges.map((c) => (
                <li key={`${c.plan}:${c.item.key}`}>
                  <FormattedMessage
                    id="admin.plans.confirmChange"
                    values={{ plan: c.plan, item: c.item.label, from: formatValue(c.item, c.from), to: formatValue(c.item, c.to), b: (chunks) => <b>{chunks}</b> }}
                  />
                </li>
              ))}
            </ul>
            <p className="muted small" style={{ margin: 0 }}>
              {t("admin.plans.confirmNote")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}
